import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ACTOR_PHRASES, actorSentenceKey } from './actor_sentence';
import Agent from 'models/agent';
import { CHANNEL_REPLY_TEXT } from 'components/lib/channel_action_text';
import ConversationItem, { getOutgoingCommunicationStatus } from 'models/conversation_item';
import ConversationItemType from 'models/conversation_item_type';
import connect from 'components/lib/connect';
import { getBrandIconByName } from 'components/common/custom_channels';
import { iconClassNameForItem } from './metadata_icon_classes';
import ItemPrepositionalPhrases from './item_prepositional_phrases';
import Metadata from './metadata';
import TranslationLanguages from 'models/languages/translation_languages';
import { typeReflect } from 'models/conversation_item/content_from_js';

export function AgentMetadata({
  agent,
  channel: channelOverride,
  currentAgentId,
  customChannels,
  hasError,
  icon,
  iconClass,
  isCollapsed,
  item,
  object,
  phrase,
  phraseComponents,
  phraseValues,
  prepositionalPhrases,
  status,
  subject,
  text,
  tooltip,
}) {
  const { t } = useTranslation();
  hasError = hasError != null ? hasError : item.content.hasErrorStatus && item.content.hasErrorStatus();
  iconClass = iconClass != null ? iconClass : iconClassNameForItem(item);
  status = status != null ? status : getOutgoingCommunicationStatus(item);

  // With neither prop supplied we own the whole "<actor> replied via <channel>"
  // sentence, so we render it from a single key instead of letting Metadata
  // concatenate an independently-translated subject and verb. Splitting them
  // cannot be translated: Spanish conjugates the verb for its subject, so
  // concatenating a translated "You" with a translated "replied via SMS"
  // produces "Tú respondió por SMS" instead of "Respondiste por SMS".
  //
  // NOTE: keep translation-function syntax out of the prose here.
  // i18next-parser lexes comments, so writing the call form would register the
  // quoted phrases as real keys and the next extract would add them to the
  // catalogs.
  //
  // A caller with its own sentence names it through `phrase`; the default is the
  // "replied via <channel>" line this component derives on its own.
  const ownsSentence = subject == null && text == null;
  const sentencePhrase = phrase || ACTOR_PHRASES.REPLIED_VIA_CHANNEL;

  const rawSubject = subject != null ? subject : getAgentName(agent, currentAgentId);
  subject = typeof rawSubject === 'string' ? t(rawSubject) : rawSubject;

  const contentType = typeReflect.instanceToType(item.content);

  let repliedViaChannel;
  const isCustomChannel = ConversationItemType.CUSTOM_CHANNEL_MESSAGE === contentType;
  if (isCustomChannel) {
    const { customChannelId } = item.content;
    const customChannel = _.find(customChannels, { id: customChannelId });

    repliedViaChannel = customChannel?.name;
    icon = <>{getBrandIconByName(customChannel?.icon, { letter: customChannel?.name, stroke: true }).icon}</>;
  } else {
    repliedViaChannel = CHANNEL_REPLY_TEXT[contentType];
  }

  let metaText;
  if (!ownsSentence) {
    metaText = typeof text === 'string' ? t(text) : text;
  } else {
    // Callers that know the channel better than CHANNEL_REPLY_TEXT does (the
    // messaging channels, whose names come off the content) pass it in rather
    // than pre-building the sentence themselves. A custom channel's name is org
    // configuration, so it is rendered as configured rather than translated.
    const channel = getChannel();
    metaText = (
      <Trans
        components={{ actor: <ActorSpan />, topics: <ObjectSpan />, ...phraseComponents }}
        i18nKey={actorSentenceKey(sentencePhrase, agent, currentAgentId)}
        values={{ agentName: agent && agent.name, channel, ...phraseValues }}
      />
    );
  }
  let language = item.translation ? TranslationLanguages[item.translation.parentLanguage] : undefined;
  return (
    <Metadata
      hasError={hasError}
      icon={icon}
      iconClass={iconClass}
      isCollapsed={isCollapsed}
      isInbound={false}
      language={language}
      object={object}
      prepositionalPhrases={prepositionalPhrases || <ItemPrepositionalPhrases item={item} />}
      status={status}
      subject={ownsSentence ? undefined : subject}
      text={metaText}
      timestamp={item.timestamp}
      tooltip={tooltip}
    />
  );

  function getChannel() {
    if (channelOverride != null) {
      return channelOverride;
    }
    if (isCustomChannel || typeof repliedViaChannel !== 'string') {
      return repliedViaChannel;
    }
    return t(repliedViaChannel);
  }
}

// Carries the same styling Metadata gives its `subject` span, so the actor stays
// bold whether it is rendered as a subject or inside a whole-sentence Trans.
function ActorSpan(props) {
  return <span className="itemMetadata-subject" {...props} />;
}

// The counterpart for a phrase that names what was acted upon. Metadata styles
// its `object` prop this way, and phrases that carry the object inside the
// sentence — so a translation can place it — keep the same weight.
function ObjectSpan(props) {
  return <span className="itemMetadata-object" {...props} />;
}

export function repliedViaKey(agent, currentAgentId) {
  return actorSentenceKey(ACTOR_PHRASES.REPLIED_VIA_CHANNEL, agent, currentAgentId);
}

export function getAgentName(agent, currentAgentId) {
  if (!agent) {
    return 'An agent';
  } else if (currentAgentId === agent.id) {
    return 'You';
  }
  return agent.name;
}

AgentMetadata.propTypes = {
  agent: PropTypes.instanceOf(Agent),
  channel: PropTypes.string,
  // One of ACTOR_PHRASES; defaults to the "replied via <channel>" line.
  phrase: PropTypes.oneOf(Object.values(ACTOR_PHRASES)),
  phraseComponents: PropTypes.object,
  phraseValues: PropTypes.object,
  currentAgentId: PropTypes.string.isRequired,
  hasError: PropTypes.bool,
  icon: PropTypes.node,
  iconClass: PropTypes.string,
  isCollapsed: PropTypes.bool,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  object: PropTypes.string,
  prepositionalPhrases: PropTypes.node,
  status: PropTypes.string,
  subject: PropTypes.node,
  text: PropTypes.any,
  tooltip: PropTypes.string,
};

function mapStateToProps({ getProvider }, props) {
  const currentAgentId = getProvider('currentAgent').get().id;
  const agent = getProvider('agents').findBy({ id: _.get(props, 'item.initiator.id') });
  const customChannels = getProvider('customChannels').findAll();

  return {
    ...props,
    agent,
    currentAgentId,
    customChannels,
  };
}

const AgentMetadataContainer = connect(mapStateToProps)(AgentMetadata);
AgentMetadataContainer.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

export default AgentMetadataContainer;
